import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Home, Plus, Trash2, Activity, Loader2, X, UserCheck, ImagePlus, Pencil, Wallet, Users2, RotateCcw, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { usePortal } from "@/context/PortalContext";
import PropertyDetailsModal from "./PropertyDetailsModal";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const CITIES = ["Mumbai", "Pune", "Kolhapur"];

const INPUT = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
const SELECT = "bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
const BTN = "inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-all shadow-sm active:scale-[0.98]";

function fileToCompressedDataUrl(file, maxDim = 1200, quality = 0.7) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim; }
        else if (height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim; }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function MyProperties() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "power_user";
  const isAdmin = user?.role === "admin";
  const { clients, selectedClientId, canSelectClient } = usePortal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showBin, setShowBin] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c.name]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = canSelectClient && selectedClientId ? `?client_id=${selectedClientId}` : "";
      const { data } = await api.get(`/properties${q}`);
      setItems(data);
    } catch (e) { toast.error("Failed to load properties"); }
    finally { setLoading(false); }
  }, [canSelectClient, selectedClientId]);
  useEffect(() => { load(); }, [load]);

  const patchLocal = (p) => setItems((list) => list.map((x) => (x.id === p.id ? p : x)));

  const remove = async (id) => {
    try { await api.delete(`/properties/${id}`); setItems(items.filter((p) => p.id !== id)); toast.success("Property removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reassign = async (id, clientId) => {
    const prev = items;
    setItems(items.map((p) => (p.id === id ? { ...p, client_id: clientId || null } : p)));
    try {
      await api.patch(`/properties/${id}`, { client_id: clientId || "" });
      toast.success(clientId ? `Assigned to ${clientMap[clientId]}` : "Unassigned");
    } catch (e) { setItems(prev); toast.error("Could not reassign"); }
  };

  const addPhotos = async (prop, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingId(prop.id);
    let ok = 0;
    for (const file of files) {
      try {
        const dataUrl = await fileToCompressedDataUrl(file);
        const { data } = await api.post(`/properties/${prop.id}/photos`, { photo: dataUrl });
        patchLocal(data); ok++;
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed"); }
    }
    setUploadingId(null);
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} added`);
  };

  const removePhoto = async (prop, url) => {
    try { const { data } = await api.post(`/properties/${prop.id}/remove-photo`, { url }); patchLocal(data); }
    catch (e) { toast.error("Could not remove photo"); }
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-mono text-emerald-700 font-semibold">Portfolio</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight font-display text-slate-900">Properties</h1>
          {!canManage && <p className="text-xs text-slate-500 mt-2 font-mono">Showing properties assigned to you. You can add photos to each.</p>}
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowBin(true)} data-testid="recycle-bin-button" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold rounded-lg px-4 py-2.5 transition-all">
              <Trash2 className="h-4 w-4" /> Recycle Bin
            </button>
            <button onClick={() => setShowAdd(true)} data-testid="add-property-button" className={`${BTN} px-5 py-2.5`}>
              <Plus className="h-4 w-4" /> Add Property
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
      ) : items.length === 0 ? (
        <div className="mt-10 p-12 text-center rounded-2xl border border-dashed border-slate-300 bg-white text-slate-500">
          <Home className="h-10 w-10 mx-auto text-slate-400" />
          <p className="mt-3">No properties yet.</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
          {items.map((p) => {
            const photos = p.photos || [];
            const owners = p.owners || [];
            return (
              <div key={p.id} data-testid="property-card"
                className="group rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-emerald-300 flex flex-col">
                <div className="aspect-video overflow-hidden bg-slate-200 relative cursor-pointer" onClick={() => setDetailsId(p.id)} data-testid="property-card-image">
                  {(p.image || photos[0]) && <img src={p.image || photos[0]} alt={p.address} className="w-full h-full object-cover" />}
                  {isAdmin && (
                    <button onClick={(e) => { e.stopPropagation(); setEditing(p); }} data-testid="edit-details-button"
                      className="absolute top-3 right-3 inline-flex items-center gap-1 text-xs bg-white/90 border border-slate-200 rounded-lg px-2.5 py-1 text-slate-700 hover:text-emerald-700 shadow-sm">
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <h3 onClick={() => setDetailsId(p.id)} data-testid="property-card-title" className="font-semibold leading-snug text-slate-900 cursor-pointer hover:text-emerald-700 transition-colors">{p.address}</h3>
                    {canManage && (
                      <button onClick={() => remove(p.id)} data-testid="delete-property-button" className="text-slate-400 hover:text-rose-600 shrink-0">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 font-mono mt-1">{p.type}</p>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    {p.valuation !== undefined ? (
                      <div>
                        <div className="text-xs text-slate-500">Current Market Value</div>
                        <div className="text-lg font-semibold text-emerald-700 font-mono">{inr(p.valuation)}</div>
                      </div>
                    ) : <div />}
                    <div className="text-right">
                      <div className="text-xs text-slate-500 flex items-center gap-1 justify-end"><Activity className="h-3 w-3" /> Health</div>
                      <div className="text-lg font-semibold font-mono text-slate-900">{p.health_score}%</div>
                    </div>
                  </div>

                  {p.monthly_maintenance ? (
                    <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                      <Wallet className="h-4 w-4 text-slate-400" /> Monthly maintenance: <span className="font-mono font-medium text-slate-900">{inr(p.monthly_maintenance)}</span>
                    </div>
                  ) : null}

                  {owners.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1"><Users2 className="h-3 w-3" /> Ownership</div>
                      <div className="mt-1 flex flex-wrap gap-1.5" data-testid="owners-list">
                        {owners.map((o, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700">{o.first_name} {o.last_name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Photo gallery */}
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono flex items-center gap-1"><ImagePlus className="h-3 w-3" /> Photos ({photos.length})</span>
                      <label data-testid="add-photos-button"
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-slate-300 text-emerald-700 hover:bg-emerald-50 cursor-pointer transition-colors">
                        {uploadingId === p.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImagePlus className="h-3 w-3" />} Add photos
                        <input type="file" accept="image/*" multiple className="hidden" data-testid="add-photos-input"
                          disabled={uploadingId === p.id}
                          onChange={(e) => { addPhotos(p, e.target.files); e.target.value = ""; }} />
                      </label>
                    </div>
                    {photos.length > 0 && (
                      <div className="mt-3 grid grid-cols-4 gap-2">
                        {photos.map((src, i) => (
                          <div key={i} className="relative group/photo aspect-square rounded-lg overflow-hidden border border-slate-200" data-testid="photo-thumb">
                            <img src={src} alt={`Property ${i + 1}`} className="w-full h-full object-cover" />
                            <button onClick={() => removePhoto(p, src)} data-testid="remove-photo-button"
                              className="absolute top-1 right-1 h-5 w-5 rounded-full bg-white/90 border border-slate-200 flex items-center justify-center text-slate-600 hover:text-rose-600 opacity-0 group-hover/photo:opacity-100 transition-opacity">
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {p.client_id && clientMap[p.client_id] ? (
                    <div className="mt-4 pt-3 border-t border-slate-200 text-[10px] font-mono text-emerald-700 flex items-center gap-1">
                      <UserCheck className="h-3 w-3" /> {clientMap[p.client_id]}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && <AddPropertyModal clients={clients} onClose={() => setShowAdd(false)} onAdded={(p) => { setItems([p, ...items]); setShowAdd(false); }} />}
      {editing && <EditDetailsModal property={editing} onClose={() => setEditing(null)} onSaved={(p) => { patchLocal(p); setEditing(null); }} />}
      {detailsId && <PropertyDetailsModal propertyId={detailsId} onClose={() => setDetailsId(null)} onUpdated={(p) => patchLocal(p)} />}
      {showBin && <RecycleBinModal onClose={() => setShowBin(false)} onChange={load} />}
    </div>
  );
}

function AddressFields({ v, onChange }) {
  const f = (k, label) => (
    <TextInput label={label} value={v[k] || ""} onChange={(val) => onChange(k, val)} testid={`addr-${k}`} />
  );
  return (
    <div className="space-y-3" data-testid="address-fields">
      {f("address_line1", "Address line 1")}
      {f("address_line2", "Address line 2")}
      {f("address_line3", "Address line 3")}
      {f("landmark", "Landmark")}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-slate-600">City *</label>
          <select required value={v.city || ""} onChange={(e) => onChange("city", e.target.value)} data-testid="addr-city" className={`${SELECT} mt-1.5 w-full px-3 py-2.5`}>
            <option value="">Select city…</option>
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {f("state", "State")}
      </div>
      <div className="grid grid-cols-2 gap-4">
        {f("postcode", "Postcode")}
        <div>
          <label className="text-sm text-slate-600">Country</label>
          <input value="India" disabled data-testid="addr-country" className={`${INPUT} bg-slate-50 text-slate-500`} />
        </div>
      </div>
    </div>
  );
}

function AddPropertyModal({ clients, onClose, onAdded }) {
  const [form, setForm] = useState({ address_line1: "", address_line2: "", address_line3: "", landmark: "", city: "", state: "", postcode: "", country: "India", type: "Residential", valuation: "", monthly_maintenance: "", status: "Occupied", health_score: 90, image: "", client_id: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/properties", { ...form, valuation: Number(form.valuation) || 0, monthly_maintenance: Number(form.monthly_maintenance) || 0, health_score: Number(form.health_score) || 90, client_id: form.client_id || null });
      toast.success("Property added");
      onAdded(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-property-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-900">Add Property</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <AddressFields v={form} onChange={(k, val) => setForm({ ...form, [k]: val })} />
          <div>
            <label className="text-sm text-slate-600">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={`${SELECT} mt-1.5 w-full px-3 py-2.5`}>
              {["Residential", "Penthouse", "Villa", "Apartment", "Commercial"].map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextInput label="Current Market Value (₹)" type="number" testid="property-valuation-input" value={form.valuation} onChange={(v) => setForm({ ...form, valuation: v })} />
            <TextInput label="Monthly Maintenance (₹)" type="number" testid="property-maintenance-input" value={form.monthly_maintenance} onChange={(v) => setForm({ ...form, monthly_maintenance: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextInput label="Health score" type="number" testid="property-health-input" value={form.health_score} onChange={(v) => setForm({ ...form, health_score: v })} />
            <TextInput label="Cover image URL" testid="property-image-input" value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
          </div>
          <div>
            <label className="text-sm text-slate-600">Assign to client</label>
            <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="property-assign-client-select" className={`${SELECT} mt-1.5 w-full px-3 py-2.5`}>
              <option value="">Unassigned</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <button type="submit" disabled={busy} data-testid="save-property-button" className={`${BTN} w-full justify-center px-6 py-3 disabled:opacity-60`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Add Property
          </button>
        </form>
      </div>
    </div>
  );
}

function EditDetailsModal({ property, onClose, onSaved }) {
  const [addr, setAddr] = useState({
    address_line1: property.address_line1 || "", address_line2: property.address_line2 || "",
    address_line3: property.address_line3 || "", landmark: property.landmark || "",
    city: property.city || "", state: property.state || "", postcode: property.postcode || "", country: "India",
  });
  const [valuation, setValuation] = useState(property.valuation ?? "");
  const [maintenance, setMaintenance] = useState(property.monthly_maintenance ?? "");
  const [owners, setOwners] = useState((property.owners || []).map((o) => ({ first_name: o.first_name || "", last_name: o.last_name || "", address: o.address || "", phone: o.phone || "", email: o.email || "" })));
  const [busy, setBusy] = useState(false);

  const addOwner = () => { if (owners.length < 3) setOwners([...owners, { first_name: "", last_name: "", address: "", phone: "", email: "" }]); };
  const removeOwner = (i) => setOwners(owners.filter((_, idx) => idx !== i));
  const setOwner = (i, k, v) => setOwners(owners.map((o, idx) => (idx === i ? { ...o, [k]: v } : o)));

  const submit = async (e) => {
    e.preventDefault();
    for (const o of owners) {
      if (!o.first_name.trim() || !o.last_name.trim()) return toast.error("Each owner needs a first and last name");
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/properties/${property.id}/details`, {
        ...addr, valuation: Number(valuation) || 0, monthly_maintenance: Number(maintenance) || 0, owners,
      });
      toast.success("Details updated");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-details-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-slate-900">Edit Property Details</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <AddressFields v={addr} onChange={(k, val) => setAddr((a) => ({ ...a, [k]: val }))} />
          <div className="grid grid-cols-2 gap-4">
            <TextInput label="Current Market Value (₹)" type="number" testid="details-value-input" value={valuation} onChange={setValuation} />
            <TextInput label="Monthly Maintenance (₹)" type="number" testid="details-maintenance-input" value={maintenance} onChange={setMaintenance} />
          </div>

          <div className="pt-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Users2 className="h-4 w-4 text-emerald-600" /> Ownership details</span>
              <button type="button" onClick={addOwner} disabled={owners.length >= 3} data-testid="add-owner-button"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
                <Plus className="h-3 w-3" /> Add owner
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">Up to 3 owners. First & last name required; address, phone and email optional.</p>

            {owners.length === 0 && <p className="mt-3 text-sm text-slate-400">No owners added yet.</p>}
            <div className="mt-3 space-y-4">
              {owners.map((o, i) => (
                <div key={i} className="p-4 rounded-xl border border-slate-200 bg-slate-50" data-testid="owner-row">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-mono text-slate-500">Owner {i + 1}</span>
                    <button type="button" onClick={() => removeOwner(i)} data-testid="remove-owner-button" className="text-slate-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <TextInput label="First name *" testid="owner-first-input" value={o.first_name} onChange={(v) => setOwner(i, "first_name", v)} required />
                    <TextInput label="Last name *" testid="owner-last-input" value={o.last_name} onChange={(v) => setOwner(i, "last_name", v)} required />
                  </div>
                  <div className="mt-3">
                    <TextInput label="Address" testid="owner-address-input" value={o.address} onChange={(v) => setOwner(i, "address", v)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <TextInput label="Phone" testid="owner-phone-input" value={o.phone} onChange={(v) => setOwner(i, "phone", v)} />
                    <TextInput label="Email" type="email" testid="owner-email-input" value={o.email} onChange={(v) => setOwner(i, "email", v)} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="save-details-button" className={`${BTN} w-full justify-center px-6 py-3 disabled:opacity-60`}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save Details
          </button>
        </form>
      </div>
    </div>
  );
}

function RecycleBinModal({ onClose, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/properties/recycle-bin"); setItems(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load recycle bin"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const daysLeft = (iso) => { const exp = new Date(new Date(iso).getTime() + 30 * 86400000); return Math.max(0, Math.ceil((exp - Date.now()) / 86400000)); };
  const restore = async (id) => {
    try { await api.post(`/properties/${id}/restore`); setItems((l) => l.filter((x) => x.id !== id)); toast.success("Property restored"); onChange && onChange(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not restore"); }
  };
  const purge = async (id) => {
    try { await api.delete(`/properties/${id}/permanent`); setItems((l) => l.filter((x) => x.id !== id)); toast.success("Permanently deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-6 shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="recycle-bin-modal">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Trash2 className="h-5 w-5 text-slate-400" /> Recycle Bin</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-slate-500 mb-5">Deleted properties are kept for 30 days, then permanently removed.</p>
        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 border border-dashed border-slate-300 rounded-xl">
            <Trash2 className="h-8 w-8 mx-auto text-slate-400" /><p className="mt-3 text-sm">Recycle bin is empty.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="recycle-bin-list">
            {items.map((p) => (
              <div key={p.id} data-testid="recycle-bin-item" className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50">
                <span className="h-11 w-11 rounded-lg overflow-hidden bg-slate-200 shrink-0">
                  {(p.image || (p.photos && p.photos[0])) && <img src={p.image || p.photos[0]} alt="" className="h-full w-full object-cover" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{p.address}</div>
                  <div className="text-xs text-slate-400 font-mono flex items-center gap-1"><Clock className="h-3 w-3" /> {daysLeft(p.deleted_at)} day(s) left</div>
                </div>
                <button onClick={() => restore(p.id)} data-testid="restore-property-button" title="Restore" className="p-2 rounded-lg text-emerald-600 hover:bg-white transition-colors"><RotateCcw className="h-4 w-4" /></button>
                <button onClick={() => purge(p.id)} data-testid="purge-property-button" title="Delete permanently" className="p-2 rounded-lg text-rose-500 hover:bg-white transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TextInput = ({ label, value, onChange, type = "text", required, testid }) => (  <div>
    <label className="text-sm text-slate-600">{label}</label>
    <input type={type} required={required} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={INPUT} />
  </div>
);
